import { useState } from "react";
import { PlaidConnectPanel } from "./PlaidConnectPanel";
import type { BuyerFundingFlowPlaidEvidence } from "../flows/buyerFunding/types";

type SourceMode = "choose" | "plaid" | "manual";

type Props = {
  fileCode: string;
  buyerName: string;
  onPlaidConnected: (evidence: BuyerFundingFlowPlaidEvidence) => void;
  onManualRecorded: (evidence: BuyerFundingFlowPlaidEvidence) => void;
  onSkip: () => void;
  onPlaidSimulate?: () => void;
};

export function BuyerSourceAccountStep({
  fileCode,
  buyerName,
  onPlaidConnected,
  onManualRecorded,
  onSkip,
  onPlaidSimulate
}: Props) {
  const [mode, setMode] = useState<SourceMode>("choose");
  const [institutionName, setInstitutionName] = useState("");
  const [accountLast4, setAccountLast4] = useState("");
  const [routingLast4, setRoutingLast4] = useState("");
  const [ownerName, setOwnerName] = useState(buyerName);
  const [formError, setFormError] = useState("");

  if (mode === "plaid") {
    return (
      <div className="grid gap-3">
        <button className="button-secondary w-fit text-sm" type="button" onClick={() => setMode("choose")}>
          ← Back to source options
        </button>
        <PlaidConnectPanel
          fileCode={fileCode}
          buyerName={buyerName}
          onConnected={onPlaidConnected}
          onSkip={onSkip}
          onSimulate={onPlaidSimulate}
        />
      </div>
    );
  }

  if (mode === "manual") {
    function handleManualSubmit(event: React.FormEvent) {
      event.preventDefault();
      const bank = institutionName.trim();
      const last4 = accountLast4.replace(/\D/g, "").slice(-4);
      if (!bank || last4.length !== 4) {
        setFormError("Enter your bank name and the last four digits of the account you will pay from.");
        return;
      }
      setFormError("");
      onManualRecorded({
        mode: "manual",
        connectedAt: new Date().toISOString(),
        ownerName: ownerName.trim() || buyerName,
        institutionName: bank,
        accountLast4: last4,
        routingLast4: routingLast4.replace(/\D/g, "").slice(-4) || undefined,
        limitation:
          "Buyer-entered source account details for office review only. Veto does not verify this account or approve the payment."
      });
    }

    return (
      <section className="panel" data-testid="manual-source-account">
        <button className="button-secondary mb-3 text-sm" type="button" onClick={() => setMode("choose")}>
          ← Back
        </button>
        <form className="grid gap-3" onSubmit={handleManualSubmit}>
          <label className="grid gap-1">
            <span className="field-label">Bank name</span>
            <input
              data-testid="manual-source-bank"
              value={institutionName}
              onChange={(e) => setInstitutionName(e.target.value)}
              placeholder="e.g. Chase"
            />
          </label>
          <label className="grid gap-1">
            <span className="field-label">Account holder name</span>
            <input
              data-testid="manual-source-owner"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
          </label>
          <label className="grid gap-1">
            <span className="field-label">Account last four</span>
            <input
              data-testid="manual-source-account-last4"
              inputMode="numeric"
              maxLength={4}
              value={accountLast4}
              onChange={(e) => setAccountLast4(e.target.value)}
              placeholder="1234"
            />
          </label>
          <label className="grid gap-1">
            <span className="field-label">Routing last four (optional)</span>
            <input
              data-testid="manual-source-routing-last4"
              inputMode="numeric"
              maxLength={4}
              value={routingLast4}
              onChange={(e) => setRoutingLast4(e.target.value)}
              placeholder="5678"
            />
          </label>
          {formError ? <p className="text-sm text-[#8a2d10]">{formError}</p> : null}
          <button className="button-primary" data-testid="manual-source-submit" type="submit">
            Save source account details
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="panel" data-testid="source-account-choice">
      <div className="grid gap-2 sm:grid-cols-2">
        <button
          className="button-primary min-h-[4.5rem] text-left"
          type="button"
          data-testid="source-choose-plaid"
          onClick={() => setMode("plaid")}
        >
          Connect with Plaid
        </button>
        <button
          className="button-secondary min-h-[4.5rem] text-left"
          type="button"
          data-testid="source-choose-manual"
          onClick={() => setMode("manual")}
        >
          Enter bank manually
        </button>
      </div>
      <button className="button-secondary mt-3 text-sm" type="button" data-testid="source-skip" onClick={onSkip}>
        Skip for now
      </button>
    </section>
  );
}
